/**
 * COSMIC 9 GEMS — EDITORIAL MOTION
 *
 * Two behaviours the About and Contact pages need that reveal-on-scroll does
 * not cover:
 *
 *   [data-c9-parallax]          drifts a layer against the scroll
 *   [data-c9-count="1200"]      counts a statistic up when it is first seen
 *
 * Both are progressive enhancement. Under prefers-reduced-motion this file
 * does nothing at all beyond writing the final value of each counter, so a
 * statistic still reads correctly — it simply does not animate. Nothing here
 * ever controls whether content is visible; c9-reveal.js owns that, and it
 * makes the same guarantee.
 *
 * Parallax runs off one requestAnimationFrame loop shared by every layer, and
 * the loop only runs while at least one layer is on screen, so an About page
 * that has scrolled past its hero costs nothing.
 */

(function () {
  'use strict';

  // Four sections on the About page each request this file. A browser fetches
  // it once but executes every duplicate <script src>, which would mean four
  // observers and four scroll listeners doing the same work. One instance is
  // enough; the rest return here.
  if (window.C9Motion) return;

  var PARALLAX_SELECTOR = '[data-c9-parallax]';
  var COUNT_SELECTOR = '[data-c9-count]';

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ======================================================================
     Counters
     ====================================================================== */

  /**
   * Format a number the way the authored value was written, so "2,400" counts
   * up with its separator and "98" does not gain one.
   */
  function formatNumber(value, grouped) {
    var rounded = String(Math.round(value));
    if (!grouped) return rounded;
    return rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function runCounter(element) {
    var target = parseFloat(element.getAttribute('data-c9-count'));
    if (isNaN(target)) return;

    var grouped = element.getAttribute('data-c9-count-grouped') === 'true';
    var prefix = element.getAttribute('data-c9-count-prefix') || '';
    var suffix = element.getAttribute('data-c9-count-suffix') || '';

    function write(value) {
      element.textContent = prefix + formatNumber(value, grouped) + suffix;
    }

    if (prefersReducedMotion) {
      write(target);
      return;
    }

    var duration = 1400;
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / duration, 1);
      // Ease-out cubic: fast to begin, settling rather than stopping.
      var eased = 1 - Math.pow(1 - t, 3);
      write(target * eased);
      if (t < 1) requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  /* ======================================================================
     Parallax
     ====================================================================== */

  var layers = [];
  var visible = 0;
  var ticking = false;

  function positionLayer(layer) {
    var box = layer.element.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;

    // Progress runs -1 (element below the fold) to 1 (element above it),
    // measured from the element's centre so the drift is symmetrical and the
    // layer sits at its authored position when centred in the viewport.
    var centre = box.top + box.height / 2;
    var progress = (centre - viewport / 2) / (viewport / 2 + box.height / 2);
    progress = Math.max(-1, Math.min(1, progress));

    layer.element.style.setProperty(
      '--c9-parallax-y',
      (progress * layer.depth).toFixed(2) + 'px'
    );
  }

  function update() {
    ticking = false;
    for (var i = 0; i < layers.length; i += 1) {
      if (layers[i].visible) positionLayer(layers[i]);
    }
  }

  function requestUpdate() {
    if (ticking || visible === 0) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  /* ======================================================================
     Wiring
     ====================================================================== */

  var counterObserver = null;
  var parallaxObserver = null;

  if ('IntersectionObserver' in window) {
    counterObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          runCounter(entry.target);
          counterObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    parallaxObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var layer = null;
          for (var i = 0; i < layers.length; i += 1) {
            if (layers[i].element === entry.target) layer = layers[i];
          }
          if (!layer) return;
          if (entry.isIntersecting === layer.visible) return;

          layer.visible = entry.isIntersecting;
          visible += entry.isIntersecting ? 1 : -1;
          if (entry.isIntersecting) positionLayer(layer);
        });
        requestUpdate();
      },
      { rootMargin: '20% 0px 20% 0px', threshold: 0 }
    );
  }

  function scan(root) {
    var scope = root || document;

    Array.prototype.forEach.call(
      scope.querySelectorAll(COUNT_SELECTOR),
      function (element) {
        if (element.dataset.c9CountBound === 'true') return;
        element.dataset.c9CountBound = 'true';

        if (!counterObserver) {
          runCounter(element);
          return;
        }
        counterObserver.observe(element);
      }
    );

    // Parallax is a desktop-and-up refinement. On a phone the viewport is too
    // short for the drift to read as anything but jitter, and the scroll is
    // touch-driven, where a per-frame transform is exactly the wrong cost.
    if (prefersReducedMotion || !parallaxObserver) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    Array.prototype.forEach.call(
      scope.querySelectorAll(PARALLAX_SELECTOR),
      function (element) {
        if (element.dataset.c9ParallaxBound === 'true') return;
        element.dataset.c9ParallaxBound = 'true';

        var depth = parseFloat(element.getAttribute('data-c9-parallax'));
        if (isNaN(depth) || depth === 0) depth = 28;

        layers.push({ element: element, depth: depth, visible: false });
        parallaxObserver.observe(element);
      }
    );
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  window.C9Motion = { scan: scan };
})();
